import React from 'react';

import { Box } from '@lightspeed/flame/Core';
import { useQuery } from '@tanstack/react-query';

import Format from 'helpers/format';
import { createReactRenderer } from 'helpers/react-renderer';
import {
  ERROR_MESSAGES,
  ROW_LABELS,
} from 'root/apps/QuoteMachineIntegration/components/PaymentRequest/TotalsSection/messages';
import { PaymentRequestListingLink } from 'root/apps/QuoteMachineIntegration/components/PaymentRequest/TotalsSection/PaymentRequestListingLink';
import { PaymentRequestTotalsRow } from 'root/apps/QuoteMachineIntegration/components/PaymentRequest/TotalsSection/TotalsRow';
import { PaymentRequestTotalsTitle } from 'root/apps/QuoteMachineIntegration/components/PaymentRequest/TotalsSection/TotalsTitle';
import PaymentRequestService from 'root/apps/QuoteMachineIntegration/services/PaymentRequestService';
import {
  PaymentRequestFilters,
  PaymentRequestListResponse,
  PaymentRequestStatus,
} from 'root/apps/QuoteMachineIntegration/state/PaymentRequest';
import Modules from 'root/apps/shared_modules/Modules';

type Props = {
  customerRef?: number;
  isDisplayingWorkOrder?: boolean;
  onRegisterPage?: boolean;
};

const PaymentRequestTotalsSection: React.FC<Props> = ({
  customerRef = 0,
  isDisplayingWorkOrder = false,
  onRegisterPage = false,
}) => {
  // hide work order's online deposit section when open in register
  const hideWhenInRegister = onRegisterPage && isDisplayingWorkOrder;

  // hide collected row when user doesn't have the invoicing module enabled
  const hasInvoicingModule = Modules.hasModule('customers.invoicing_integration.free_tier');
  const hideCollected = !hasInvoicingModule;

  const hasPaymentRequest = Modules.hasModule('customers.invoicing_integration.payment_request');

  // initialize filters for fetching payment requests
  const sentFilters: PaymentRequestFilters = {
    status: PaymentRequestStatus.SENT,
    customerRef,
  };

  // fetch pending payment request total
  const {
    data: pendingPayReqs,
    isLoading: loadingPendingPayReqs,
    isError: errorFetchingPendingPayReqs,
  } = useQuery<PaymentRequestListResponse>({
    queryKey: ['paymentRequestTotals', customerRef],
    queryFn: () => PaymentRequestService.fetchPaymentRequestsWithFilters(sentFilters),
    retry: false,
    cacheTime: 0,
    refetchOnMount: true,
    enabled: !!customerRef && !hideWhenInRegister,
  });

  // fetch collected payment requests total
  const {
    data: collectedPayReqs,
    isLoading: loadingCollectedPayReqs,
    isError: errorFetchingCollectedPayReqs,
  } = useQuery({
    queryKey: ['collectedPaymentRequests', customerRef],
    queryFn: () => PaymentRequestService.fetchCollectedPaymentRequestsTotal(customerRef.toString()),
    retry: false,
    cacheTime: 0,
    refetchOnMount: true,
    enabled: !!customerRef && !hideWhenInRegister && !hideCollected && !hasPaymentRequest,
  });

  // error flags for conditional rendering
  const onlyHasPendingErr = errorFetchingPendingPayReqs && !errorFetchingCollectedPayReqs;
  const onlyHasCollectedErr = !errorFetchingPendingPayReqs && errorFetchingCollectedPayReqs;
  const errorFetchingAllTotals = errorFetchingPendingPayReqs && errorFetchingCollectedPayReqs;

  // initialize totals from response
  const collectedAmountTotal = Number(collectedPayReqs?.data);
  const pendingAmountTotal = Number(pendingPayReqs?.meta.totalAmount) / 100 || 0;

  // hide section if there are no totals to show
  const showTotals = pendingAmountTotal > 0 || collectedAmountTotal > 0;
  if (hideWhenInRegister || !showTotals) {
    return null;
  }

  return (
    <Box
      style={{
        borderBottom: onRegisterPage ? '1px solid #C4CACC' : 'none',
        paddingBottom: hasPaymentRequest ? '0' : '8px',
      }}
    >
      {hasPaymentRequest ? (
        <PaymentRequestListingLink
          pendingAmountTotal={Format.toMoney(pendingAmountTotal)}
          onRegisterPage={onRegisterPage}
        />
      ) : (
        <>
          <PaymentRequestTotalsTitle
            onRegisterPage={onRegisterPage}
            showWarning={errorFetchingAllTotals}
          />
          {!hideCollected && (
            <PaymentRequestTotalsRow
              label={ROW_LABELS.COLLECTED}
              loading={loadingCollectedPayReqs}
              value={Format.toMoney(collectedAmountTotal)}
              notAvailable={errorFetchingAllTotals || onlyHasCollectedErr}
              showWarning={onlyHasCollectedErr}
              warningToolTipText={ERROR_MESSAGES.FETCH_COLLECTED_PAY_REQS_TOTAL_ERROR}
            />
          )}
          <PaymentRequestTotalsRow
            label={ROW_LABELS.PENDING}
            loading={loadingPendingPayReqs}
            value={Format.toMoney(pendingAmountTotal)}
            notAvailable={errorFetchingAllTotals || onlyHasPendingErr}
            showWarning={onlyHasPendingErr}
            warningToolTipText={ERROR_MESSAGES.FETCH_PENDING_PAY_REQS_TOTAL_ERROR}
          />
        </>
      )}
    </Box>
  );
};

export default PaymentRequestTotalsSection;

const RenderedComponent = createReactRenderer({
  component: PaymentRequestTotalsSection,
  selector: '[data-react-root="PaymentRequestTotalsSection"]',
});

history.onChangeState(
  [
    { pathname: '/register.php', form_name: undefined, view: undefined },
    { name: 'workbench.views.beta_workorder', form_name: 'view' },
  ],
  (filters: HistoryFilter) => {
    const onRegisterPage = filters?.pathname === '/register.php';

    RenderedComponent.mount({
      props: { onRegisterPage },
    });
  },
  () => {
    RenderedComponent.unmount();
  }
);
